import React from "react";
import { Tag, Tooltip } from "antd";
import { Map, Globe, Users, Settings, Star, Zap, Eye, Shield } from "lucide-react";
import {
  getPermissionLabel,
  getPermissionCategory,
  groupPermissionsByCategory,
  PERMISSION_CATEGORIES,
  PermissionCategory,
} from "../../../constants/permissions";

interface PermissionBadgeProps {
  slug: string;
  size?: "small" | "default";
}

const CATEGORY_ICONS: Record<PermissionCategory, React.ReactNode> = {
  maps: <Map size={12} />,
  newsletters: <Globe size={12} />,
  management_roles: <Shield size={12} />,
  management_actions: <Zap size={12} />,
  management_view: <Eye size={12} />,
  subscriptions: <Star size={12} />,
  purchases: <Star size={12} />,
  other: <Settings size={12} />,
};

/**
 * Badge individual de un permiso con color por categoría e icono.
 */
export function PermissionBadge({ slug, size = "default" }: PermissionBadgeProps) {
  const category = getPermissionCategory(slug);
  const label = getPermissionLabel(slug);
  const config = PERMISSION_CATEGORIES[category];

  return (
    <Tooltip title={slug} placement="top">
      <Tag
        icon={CATEGORY_ICONS[category]}
        style={{
          borderColor: config.color + "55",
          backgroundColor: config.bgColor,
          color: config.color,
          fontSize: size === "small" ? "11px" : "12px",
          padding: size === "small" ? "1px 6px" : "2px 8px",
          borderRadius: "6px",
          marginBottom: "4px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
}

interface PermissionBadgeListProps {
  permissions: string[];
  grouped?: boolean;
  size?: "small" | "default";
  maxVisible?: number;
}

/**
 * Lista de badges de permisos, opcionalmente agrupados por categoría.
 */
export function PermissionBadgeList({
  permissions,
  grouped = false,
  size = "default",
  maxVisible,
}: PermissionBadgeListProps) {
  if (!permissions || permissions.length === 0) {
    return (
      <span style={{ color: "#999", fontStyle: "italic", fontSize: "12px" }}>
        Sin permisos asignados
      </span>
    );
  }

  if (!grouped) {
    const visible = maxVisible ? permissions.slice(0, maxVisible) : permissions;
    const remaining = maxVisible ? permissions.length - maxVisible : 0;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
        {visible.map((slug) => (
          <PermissionBadge key={slug} slug={slug} size={size} />
        ))}
        {remaining > 0 && (
          <Tooltip
            title={permissions
              .slice(maxVisible)
              .map((s) => getPermissionLabel(s))
              .join(", ")}
          >
            <Tag
              style={{
                borderRadius: "6px",
                fontSize: "11px",
                marginBottom: "4px",
                cursor: "pointer",
              }}
            >
              +{remaining} más
            </Tag>
          </Tooltip>
        )}
      </div>
    );
  }

  // Agrupados por categoría
  const groups = groupPermissionsByCategory(permissions);
  const categoryOrder: PermissionCategory[] = [
    "maps",
    "newsletters",
    "management_roles",
    "management_actions",
    "management_view",
    "subscriptions",
    "purchases",
    "other",
  ];

  return (
    <div>
      {categoryOrder.map((cat) => {
        const slugs = groups[cat];
        if (!slugs || slugs.length === 0) return null;
        const config = PERMISSION_CATEGORIES[cat];

        return (
          <div key={cat} style={{ marginBottom: "8px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "4px",
              }}
            >
              <span style={{ color: config.color }}>{CATEGORY_ICONS[cat]}</span>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: config.color,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {config.label}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "2px", paddingLeft: "4px" }}>
              {slugs.map((slug: string) => (
                <PermissionBadge key={slug} slug={slug} size={size} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PermissionBadge;
